import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

interface AddSpeakerSectionModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: {
    speaker_id: string;
    text: string;
    duration: string;
    timestamp?: string;
  }) => Promise<void>;
  availableSpeakerIds: string[];
  isLoading?: boolean;
}

// Get default speaker ID - use first available or "Guest-1" as fallback
const getDefaultSpeakerId = (availableIds: string[]): string => {
  if (availableIds.length > 0) {
    return availableIds[0];
  }
  return "Guest-1";
};

const AddSpeakerSectionModal: React.FC<AddSpeakerSectionModalProps> = ({
  open,
  onClose,
  onSave,
  availableSpeakerIds,
  isLoading = false,
}) => {
  const [text, setText] = useState("");
  const [errors, setErrors] = useState<{
    text?: string;
  }>({});

  // Get default speaker ID
  const defaultSpeakerId = getDefaultSpeakerId(availableSpeakerIds);

  useEffect(() => {
    if (open) {
      // Reset form when modal opens
      setText("");
      setErrors({});
    }
    // Only reset when modal opens, not when availableSpeakerIds changes
    // availableSpeakerIds changes frequently during live streaming and would clear user input
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSave = async () => {
    const newErrors: { text?: string } = {};

    if (!text.trim()) {
      newErrors.text = "Text is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      await onSave({
        speaker_id: defaultSpeakerId,
        text: text.trim(),
        duration: "00:00", // Default duration
        timestamp: new Date().toISOString(),
      });
      onClose();
    } catch (error) {
      console.error("Error saving speaker section:", error);
      // Error is handled by parent component
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        // Only allow closing via Cancel button
        // Disable backdrop click and ESC key
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
          zIndex: 100000, // Dialog content above backdrop
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.5) !important", // Dark overlay to hide background
            zIndex: 99999, // Backdrop above other content including Floating Edit Summary popup
          },
        },
      }}
      sx={{
        zIndex: 99999, // Dialog container z-index
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(0, 0, 0, 0.5) !important", // Ensure dark backdrop
          zIndex: "99999 !important", // Force backdrop above everything
        },
        "& .MuiDialog-container": {
          zIndex: 99999,
        },
        "& .MuiDialog-paper": {
          zIndex: 100000, // Dialog content above backdrop
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AddIcon />
          <Typography variant="h6">Add New Speaker Section</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            label="Text"
            multiline
            rows={6}
            fullWidth
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors.text;
                return newErrors;
              });
            }}
            error={!!errors.text}
            helperText={errors.text || "Enter the transcription text for this section"}
            required
            disabled={isLoading}
            placeholder="Enter speaker text here..."
            autoFocus
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isLoading || !text.trim()}
          startIcon={isLoading ? <CircularProgress size={16} /> : <AddIcon />}
        >
          {isLoading ? "Adding..." : "Add Section"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddSpeakerSectionModal;

